import { defineStore } from 'pinia'
import { string as yupString, object as yupObject } from 'yup';
import { useForm } from 'vee-validate';

// In Setup Stores:

//     ref()s      become state properties
//     computed()s become getters
//     function()s become actions

const zipPatterns = {
  US: /^\d{5}$/, // strict 5-digit (matches server-side validation in class-clover-data-helper.php)
  // CA: /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/, // A1A 1A1 (канадский)
  // UK: /^[A-Za-z]{1,2}\d{1,2}[A-Za-z]? \d[A-Za-z]{2}$/, // (UK Royal Mail)
  // DE: /^\d{5}$/, // Германия
  // FR: /^\d{5}$/, // Франция
  // IT: /^\d{5}$/, // Италия
  // ES: /^\d{5}$/, // Испания
  // NL: /^\d{4} ?[A-Za-z]{2}$/, // Нидерланды
  // PT: /^\d{4}-\d{3}$/, // Португалия
  // CH: /^\d{4}$/, // Швейцария
  // BE: /^\d{4}$/, // Бельгия
  // AU: /^\d{4}$/, // Австралия
  // AT: /^\d{4}$/, // Австрия
  // SE: /^\d{3} ?\d{2}$/, // Швеция
  // NO: /^\d{4}$/, // Норвегия
  // DK: /^\d{4}$/, // Дания
  // FI: /^\d{5}$/, // Финляндия
  // CZ: /^\d{3} ?\d{2}$/, // Чехия
  // SK: /^\d{3} ?\d{2}$/, // Словакия
  // PL: /^\d{2}-\d{3}$/, // Польша
  // HU: /^\d{4}$/, // Венгрия
  // RO: /^\d{6}$/  // Румыния
};

// persisted, v2
const validationSchema = yupObject({
  instructions: yupString()
    .label('Special Instructions'),
  orderType: yupString()
    .required()
    .oneOf(['delivery', 'takeout', 'catering'])
    .default('delivery'),
  paymentOption: yupString()
    .required()
    .oneOf(['card', 'cash'])
    .default('card'),
  // paymentTime: yupString()
  // .required()
  // .oneOf(['now', 'later'])
  // .default('now'),
  firstName: yupString()
    .required()
    .label('First Name'),
  lastName: yupString()
    .required()
    .label('Last Name'),
  phoneNumber: yupString()
    .required()
    .min(10)
    .label('Phone Number')
    .matches(/^\+1\s\(\d{3}\)\s\d{3}-\d{4}$/, 'Phone format +1 (XXX) XXX-XXXX')
    // The mask placeholder "+1 (000) 000-0000" matches the format above (000 are
    // valid digits), so it would pass as a real number and then be rejected by
    // Uber. Reject an all-same-digit number so the customer enters a real one.
    .test('not-placeholder', 'Enter a valid phone number', (v) =>
      !v || !/^(\d)\1{9}$/.test(v.replace(/\D/g, '').replace(/^1/, ''))),
  email: yupString()
    .required()
    .email()
    .label('Email'),
  street: yupString()
    .label('Address')
    .when('orderType', {
      is: (val) => val == 'delivery',
      then: () => yupString().required('Address is required'),
    }),
  city: yupString()
    .label('City')
    .when('orderType', {
      is: (val) => val == 'delivery',
      then: () => yupString().required('City is required'),
    }),
  state: yupString()
    .label('State or Province')
    .when('orderType', {
      is: (val) => val == 'delivery',
      then: () => yupString().required('State is required'),
    }),
  zip: yupString()
    .label('Postal code')
    .when('orderType', {
      is: (val) => val == 'delivery',
      then: () => yupString()
        .required('ZIP is required')
        .test('is-valid-zip', 'Incorrect ZIP', (value) => {
          if (!value) return false;
          return Object.values(zipPatterns).some((pattern) => pattern.test(value));
        }),
    }),
});

export const useCustomerStore = defineStore('customer', {
  state: () => {
    let defaultOrderType = window['_EZCL']?.data?.defaultService && window['_EZCL']?.data?.services && window['_EZCL']?.data?.services[window['_EZCL']?.data?.defaultService] ? window['_EZCL']?.data?.defaultService : null;
    const { meta, errors, defineField } = useForm({
      validateOnMount: true,
      validationSchema: validationSchema,
      initialValues: {
        orderType: defaultOrderType || 'delivery',
        paymentOption: 'card',
      }
    });

    const [firstName, firstNameProps] = defineField('firstName');
    const [lastName, lastNameProps] = defineField('lastName');
    const [phoneNumber, phoneNumberProps] = defineField('phoneNumber');
    const [email, emailProps] = defineField('email');
    const [street, streetProps] = defineField('street');
    const [city, cityProps] = defineField('city');
    const [state, stateProps] = defineField('state');
    const [zip, zipProps] = defineField('zip');
    const [orderType, orderTypeProps] = defineField('orderType');
    const [paymentOption, paymentOptionProps] = defineField('paymentOption');
    // const [ paymentTime, paymentTimeProps ] = defineField('paymentTime');
    const [instructions, instructionsProps] = defineField('instructions');

    const customerId = '';

    // Captured from Google autocomplete; first-class data for the radius gate
    // and later Uber Direct. Not vee-validate fields — these are not user-typed.
    const lat = null;
    const lng = null;
    const placeId = '';
    const formattedAddress = '';

    // Delivery-zone status, driven by DeliveryFields via useDeliveryZone.
    // Permissive default; only 'outside' blocks the order.
    const zoneStatus = 'n/a';

    // Live Uber Direct quote for the current address (transient, like
    // zoneStatus — recomputed from the address, never persisted).
    const uberQuote = { status: 'idle', feeCents: null, etaMinutes: null, quoteId: null };

    return {
      errors,
      meta,
      firstName,
      lastName,
      phoneNumber,
      email,
      street,
      city,
      state,
      zip,
      lat,
      lng,
      placeId,
      formattedAddress,
      zoneStatus,
      uberQuote,
      customerId,
      orderType,
      paymentOption,
      // paymentTime,
      instructions,
      firstNameProps,
      lastNameProps,
      phoneNumberProps,
      emailProps,
      streetProps,
      cityProps,
      stateProps,
      zipProps,
      orderTypeProps,
      paymentOptionProps,
      // paymentTimeProps,
      instructionsProps,
    }
  },
  getters: {
    isDeliveryOrder(){
      return (this?.orderType == 'delivery');
    },
    // True when this delivery uses the Uber Direct provider. Uber delivers
    // ASAP only (no scheduled windows yet), so callers hide the date/time
    // picker and show an ETA instead. The provider lives in the current order
    // type's nested `delivery` capabilities object (per frontend.php).
    isUberDelivery(){
      return this.isDeliveryOrder
        && window?._EZCL?.data?.services?.[this.orderType]?.delivery?.provider === 'uber_direct';
    },
    isCompleteCustomerData(){
          return (
            this.firstName?.trim() &&
            this.lastName?.trim() &&
            this.phoneNumber?.trim() &&
            this.email?.trim() &&
            this.customerId?.trim()
    )
    },
    deliveryBlocked(){
      return this.zoneStatus === 'outside';
    }
  },
  actions: {
    resetCustomerID() {
      this.customerId = '';
    },
    // Fill form fields + coords from a canonical address (autocomplete select).
    applyAddress(addr) {
      this.street = addr.street;
      this.city = addr.city;
      this.state = addr.state;
      this.zip = addr.zip;
      this.lat = addr.lat;
      this.lng = addr.lng;
      this.placeId = addr.placeId;
      this.formattedAddress = addr.formattedAddress;
    },
    // Manual edits invalidate Google-derived coords so the radius gate fails
    // open instead of trusting a stale location.
    clearAddressCoords() {
      this.lat = null;
      this.lng = null;
      this.placeId = '';
      this.formattedAddress = '';
    }
  },
  persist: {
    pick: [, 'firstName', 'lastName', 'phoneNumber', 'email', 'street', 'city', 'state', 'zip', 'lat', 'lng', 'placeId', 'formattedAddress', 'customerId', 'orderType', 'instructions'], // 'paymentOption'
    key: `_ezcl/${window.location.hostname}/${window['_EZCL']?.data.id}/${window['_EZCL']?.data.pageId}/customer`,
  },
});